/**
 * RETOBLOCK PROJECT FILE MANAGER (.reto SPECIFICATION)
 * Quản lý vòng đời dự án, đóng gói và giải nén định dạng container nén ZIP (.reto).
 */

import JSZip from 'jszip';
import { BlockCompiler } from './block-compiler.js';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export class ProjectManager {
  constructor() {
    this.compiler = new BlockCompiler();
    this.currentProject = this.createDefaultProject('Du_An_Robot_1', 'scratch', 'RETOLOGIX-BASIC');
    this.isDirty = false;
    this.onDirtyChange = null;
  }

  createDefaultProject(name = 'Du_An_Robot_1', languageMode = 'scratch', targetKit = 'RETOLOGIX-BASIC') {
    const now = new Date().toISOString();
    return {
      metadata: {
        schema_version: '1.0.0',
        app_version: '1.0.0',
        project_id: generateUUID(),
        project_name: name,
        target_kit: targetKit,
        language_mode: languageMode,
        author: 'Hoc_Vien_STEM',
        created_at: now,
        updated_at: now
      },
      blocksData: languageMode === 'scratch' ? {
        canvas: { scrollX: 100, scrollY: 60, scale: 1.0 },
        variables: [],
        blocks: {
          'root_flag': {
            opcode: 'event_whenflagclicked',
            topLevel: true,
            x: 80,
            y: 50,
            next: 'block_fwd_demo'
          },
          'block_fwd_demo': {
            opcode: 'reto_movement_direction_speed',
            parent: 'root_flag',
            fields: { DIRECTION: 'FORWARD' },
            inputs: { SPEED: { value: 60 } },
            next: 'block_wait_demo'
          },
          'block_wait_demo': {
            opcode: 'control_wait',
            parent: 'block_fwd_demo',
            inputs: { SECONDS: { value: 1 } },
            next: 'block_stop_demo'
          },
          'block_stop_demo': {
            opcode: 'reto_movement_stop',
            parent: 'block_wait_demo'
          }
        }
      } : null,
      pythonSource: languageMode === 'python' ? `# ==============================================================================
# RETOLOGIX STUDIO - PYTHON PROJECT
# Target Kit: RETOCAR-STARTER (ESP32)
# ==============================================================================
from retologix import RetoCar
import time

robot = RetoCar()

try:
    # Xe tiến 1 giây rồi dừng
    robot.move(direction="FORWARD", speed=60)
    time.sleep(1.0)
    robot.stop()

except KeyboardInterrupt:
    robot.stop()
    robot.buzzer_off()
` : ''
    };
  }

  setDirty(dirty = true) {
    if (this.isDirty !== dirty) {
      this.isDirty = dirty;
      if (this.onDirtyChange) this.onDirtyChange(dirty);
    }
  }

  getProjectName() {
    return this.currentProject.metadata.project_name;
  }

  setProjectName(name) {
    if (!name || !name.trim()) return;
    this.currentProject.metadata.project_name = name.trim();
    this.setDirty(true);
  }

  getLanguageMode() {
    return this.currentProject.metadata.language_mode;
  }

  getTargetKit() {
    return this.currentProject.metadata.target_kit;
  }

  getPythonCode() {
    if (this.currentProject.metadata.language_mode === 'scratch') {
      if (this.currentProject.pythonSource && this.currentProject.pythonSource.trim()) {
        return this.currentProject.pythonSource;
      }
      return this.compiler.compile(
        this.currentProject.blocksData,
        this.currentProject.metadata.project_name
      );
    } else {
      return this.currentProject.pythonSource || '';
    }
  }

  updatePythonSource(source) {
    this.currentProject.pythonSource = source;
    this.setDirty(true);
  }

  updateBlocksData(blocksData) {
    if (this.currentProject.metadata.language_mode === 'scratch') {
      this.currentProject.blocksData = blocksData;
      this.setDirty(true);
    }
  }

  /**
   * Đóng gói dự án thành tệp ZIP .reto và tải về máy tính
   */
  async exportRetoFile() {
    const zip = new JSZip();
    const now = new Date().toISOString();
    this.currentProject.metadata.updated_at = now;

    // 1. project.json
    zip.file('project.json', JSON.stringify(this.currentProject.metadata, null, 2));

    // 2. blocks.json (nếu là scratch)
    if (this.currentProject.metadata.language_mode === 'scratch') {
      zip.file('blocks.json', JSON.stringify(this.currentProject.blocksData, null, 2));
    }

    // 3. main.py
    const pythonCode = this.getPythonCode();
    zip.file('main.py', pythonCode);

    // 4. assets/thumbnail.png (tạo ảnh placeholder)
    const assetsFolder = zip.folder('assets');
    const dummyPngBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    assetsFolder.file('thumbnail.png', dummyPngBase64, { base64: true });

    // Tạo blob và trigger tải về
    const blob = await zip.generateAsync({ type: 'blob' });
    const filename = `${this.currentProject.metadata.project_name}.reto`;

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);

    this.setDirty(false);
    return filename;
  }

  /**
   * Đóng gói dự án thành Blob (application/octet-stream) cho Cloud Extension
   */
  async exportCurrentProjectBlob() {
    const zip = new JSZip();
    const now = new Date().toISOString();
    this.currentProject.metadata.updated_at = now;

    // 1. project.json
    zip.file('project.json', JSON.stringify(this.currentProject.metadata, null, 2));

    // 2. blocks.json (nếu là scratch)
    if (this.currentProject.metadata.language_mode === 'scratch') {
      zip.file('blocks.json', JSON.stringify(this.currentProject.blocksData, null, 2));
    }

    // 3. main.py
    const pythonCode = this.getPythonCode();
    zip.file('main.py', pythonCode);

    // 4. assets/thumbnail.png (tạo ảnh placeholder)
    const assetsFolder = zip.folder('assets');
    const dummyPngBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    assetsFolder.file('thumbnail.png', dummyPngBase64, { base64: true });

    // Tạo blob
    const blob = await zip.generateAsync({
      type: 'blob',
      mimeType: 'application/octet-stream'
    });
    this.setDirty(false);
    return blob;
  }

  /**
   * Đọc tệp .reto được mở từ máy tính
   */
  async importRetoFile(file) {
    const zip = await JSZip.loadAsync(file);

    // 1. Kiểm tra project.json
    const projectJsonFile = zip.file('project.json');
    if (!projectJsonFile) {
      throw new Error('Tệp .reto không hợp lệ: Thiếu file cấu hình project.json');
    }

    const projectMetaStr = await projectJsonFile.async('string');
    const metadata = JSON.parse(projectMetaStr);

    let blocksData = null;
    let pythonSource = '';

    // 2. Đọc blocks.json hoặc main.py theo language_mode
    if (metadata.language_mode === 'scratch') {
      const blocksFile = zip.file('blocks.json');
      if (blocksFile) {
        const blocksStr = await blocksFile.async('string');
        blocksData = JSON.parse(blocksStr);
      } else {
        throw new Error('Tệp .reto không hợp lệ: Thiếu blocks.json trong chế độ Scratch');
      }
    }

    const mainPyFile = zip.file('main.py');
    if (mainPyFile) {
      pythonSource = await mainPyFile.async('string');
    }

    this.currentProject = {
      metadata,
      blocksData,
      pythonSource
    };

    this.setDirty(false);
    return this.currentProject;
  }

  /**
   * Giải nén và nạp dự án từ Blob (Cloud Storage)
   */
  async importProjectFromBlob(blob) {
    const zip = await JSZip.loadAsync(blob);

    // 1. Kiểm tra project.json
    const projectJsonFile = zip.file('project.json');
    if (!projectJsonFile) {
      throw new Error('Tệp đám mây không hợp lệ: Thiếu file cấu hình project.json');
    }

    const projectMetaStr = await projectJsonFile.async('string');
    const metadata = JSON.parse(projectMetaStr);

    let blocksData = null;
    let pythonSource = '';

    // 2. Đọc blocks.json hoặc main.py theo language_mode
    if (metadata.language_mode === 'scratch') {
      const blocksFile = zip.file('blocks.json');
      if (blocksFile) {
        const blocksStr = await blocksFile.async('string');
        blocksData = JSON.parse(blocksStr);
      } else {
        throw new Error('Tệp đám mây không hợp lệ: Thiếu blocks.json trong chế độ Scratch');
      }
    }

    const mainPyFile = zip.file('main.py');
    if (mainPyFile) {
      pythonSource = await mainPyFile.async('string');
    }

    this.currentProject = {
      metadata,
      blocksData,
      pythonSource
    };

    this.setDirty(false);
    return this.currentProject;
  }

  loadSample(sampleKey, sampleProject) {
    const now = new Date().toISOString();
    this.currentProject = {
      metadata: {
        schema_version: '1.0.0',
        app_version: '1.0.0',
        project_id: generateUUID(),
        project_name: sampleProject.name,
        target_kit: sampleProject.target_kit,
        language_mode: sampleProject.language_mode,
        author: sampleProject.author || 'RETOLOGIX Master',
        created_at: now,
        updated_at: now
      },
      blocksData: sampleProject.blocksData ? JSON.parse(JSON.stringify(sampleProject.blocksData)) : null,
      blocklyData: sampleProject.blocklyData ? JSON.parse(JSON.stringify(sampleProject.blocklyData)) : null,
      pythonSource: sampleProject.pythonSource || ''
    };
    this.setDirty(false);
    return this.currentProject;
  }
}
